import styled, { createGlobalStyle } from 'styled-components';

/**
 * Global style to override Ant Design Drawer padding
 * Must be more specific than Ant's default selector
 */
export const DrawerGlobalStyles = createGlobalStyle`
  .ant-drawer .ant-drawer-body {
    padding: 0 !important;
  }
`;

/**
 * Modern Sleek Filter Design
 */
export const FiltersContainer = styled.section`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f9fafb;

  /* Filter Header - Sticky */
  .filter-header {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #6846d1 0%, #8b5cf6 100%);
    z-index: 100;
    padding: 14px;
    box-shadow: 0 2px 8px rgba(104, 70, 209, 0.15);

    h3 {
      font-size: 0.95rem;
      font-weight: 700;
      color: #ffffff;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      letter-spacing: 0.3px;

      .filter-icon {
        color: #ffffff;
        font-size: 1rem;
        margin-right: 8px;
        opacity: 0.95;
      }

      .header-text {
        display: flex;
        align-items: center;
      }
    }

    .filter-count {
      background: rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(10px);
      color: white;
      padding: 3px 10px;
      border-radius: 16px;
      font-size: 0.65rem;
      font-weight: 700;
      min-width: 24px;
      text-align: center;
      border: 1px solid rgba(255, 255, 255, 0.3);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  /* Filter Content Area */
  .filters-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 10px;
    background: #f9fafb;

    /* Custom Scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #e5e7eb;
      border-radius: 10px;
      margin: 8px 0;
    }

    &::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, #6846d1 0%, #8b5cf6 100%);
      border-radius: 10px;

      &:hover {
        background: linear-gradient(180deg, #5435b8 0%, #7c3aed 100%);
      }
    }
  }

  /* Filter Item Container */
  .filter-item {
    background: #ffffff;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;
    border: 1px solid #e5e7eb;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: visible; /* Changed from hidden to allow dropdown to escape */

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background: linear-gradient(180deg, #6846d1 0%, #8b5cf6 100%);
      opacity: 0;
      transition: opacity 0.25s ease;
    }

    /* Boost z-index when dropdown is open */
    &:has(.dropdown-menu) {
      z-index: 10000 !important; /* Use !important to ensure it's above all siblings */
    }

    /* Only apply hover effects when dropdown is NOT open */
    &:hover:not(:has(.dropdown-menu)) {
      border-color: #c4b5fd;
      box-shadow: 0 4px 12px rgba(104, 70, 209, 0.08);
      transform: translateX(2px);

      &::before {
        opacity: 1;
      }

      .title {
        color: #6846d1;
      }
    }

    .title {
      font-size: 0.75rem;
      font-weight: 700;
      color: #374151;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      letter-spacing: 0.3px;
      text-transform: uppercase;
      transition: color 0.25s ease;

      &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(
          90deg,
          rgba(104, 70, 209, 0.2) 0%,
          transparent 100%
        );
        margin-left: 10px;
      }
    }
  }

  /* Checkbox Styling */
  .ant-checkbox-wrapper {
    display: flex;
    align-items: flex-start; /* Changed from center to flex-start for better alignment */
    margin-bottom: 8px;
    padding: 7px 10px;
    border-radius: 6px;
    transition: all 0.2s ease;
    background: transparent;
    min-height: 32px; /* Ensures consistent height */

    &:hover {
      background: #f5f3ff;
    }

    &:last-child {
      margin-bottom: 0;
    }

    > span:last-child {
      font-size: 0.78rem;
      color: #4b5563;
      font-weight: 500;
      line-height: 1.5; /* Improved line height for better readability */
      padding-top: 1px; /* Fine-tune alignment with checkbox */
      word-wrap: break-word;
      flex: 1; /* Takes available space */
    }
  }

  .ant-checkbox {
    flex-shrink: 0; /* Prevents checkbox from shrinking */
    margin-top: 2px; /* Aligns checkbox with first line of text */

    .ant-checkbox-inner {
      width: 16px;
      height: 16px;
      border-radius: 4px;
      border: 2px solid #d1d5db;
      transition: all 0.25s ease;
    }

    &:hover .ant-checkbox-inner {
      border-color: #6846d1;
    }

    /* Ensure proper spacing between checkbox and label */
    + span {
      padding-left: 8px;
    }
  }

  .ant-checkbox-checked {
    .ant-checkbox-inner {
      background: linear-gradient(135deg, #6846d1 0%, #8b5cf6 100%);
      border-color: #6846d1;
    }

    &::after {
      border-color: #6846d1;
    }
  }

  /* Checkbox Group container */
  .ant-checkbox-group {
    display: flex;
    flex-direction: column;
    width: 100%;

    .ant-checkbox-group-item {
      margin-right: 0; /* Remove default margin */
      width: 100%; /* Full width for each checkbox */
    }
  }

  /* Select Dropdown */
  .ant-select {
    width: 100%;

    .ant-select-selector {
      border-radius: 6px;
      border: 2px solid #e5e7eb !important;
      transition: all 0.25s ease;
      padding: 4px 10px;
      min-height: 36px;

      &:hover {
        border-color: #c4b5fd !important;
      }
    }

    &.ant-select-focused .ant-select-selector {
      border-color: #6846d1 !important;
      box-shadow: 0 0 0 3px rgba(104, 70, 209, 0.1);
    }

    .ant-select-arrow {
      color: #6846d1;
    }
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    font-size: 0.78rem;
    color: #111827;
    line-height: 1.5;
  }

  /* Clear Filters Button - Sticky */
  .filter-actions {
    position: sticky;
    bottom: 0;
    background: #ffffff;
    padding: 12px 14px;
    border-top: 2px solid #e5e7eb;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
    z-index: 50;

    .ant-btn {
      width: 100%;
      height: 40px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      border: 2px solid #ef4444;
      background: #ffffff;
      color: #ef4444;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
        transition: left 0.35s ease;
        z-index: -1;
      }

      &:hover {
        color: white;
        border-color: #dc2626;
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(239, 68, 68, 0.3);

        &::before {
          left: 0;
        }
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  /* Empty State */
  .filter-empty {
    padding: 40px 20px;
    text-align: center;
    color: #9ca3af;
    font-size: 0.85rem;
  }

  /* Legacy classes for backward compatibility */
  .other_filters_container {
    width: 100%;
  }

  .filter_section {
    font-weight: bold;
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
  }

  /* Animation for filter items */
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .filter-item {
    animation: slideIn 0.3s ease-out backwards;

    &:nth-child(1) {
      animation-delay: 0.05s;
    }
    &:nth-child(2) {
      animation-delay: 0.1s;
    }
    &:nth-child(3) {
      animation-delay: 0.15s;
    }
    &:nth-child(4) {
      animation-delay: 0.2s;
    }
    &:nth-child(5) {
      animation-delay: 0.25s;
    }
  }

  .filter_section {
    font-weight: bold;
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
  }

  /* Custom Single Select Dropdown Styling for Filters */
  /* Override default styles to match filter theme */
  .filter-item .dropdown-control {
    min-height: 36px;
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.78rem;
    background: #ffffff;
    transition: all 0.25s ease;

    &:hover:not(.open) {
      border-color: #c4b5fd; /* Light purple hover only when not open */
      background: #fafafa;
    }

    &.open {
      border-color: #6846d1; /* Solid purple when open */
      box-shadow: 0 0 0 3px rgba(104, 70, 209, 0.08);
      background: #faf9fe; /* Very subtle purple tint */
    }
  }

  .filter-item .dropdown-title {
    font-size: 0.78rem;
    color: #111827;
    font-weight: 500;
    line-height: 1.5;
  }

  .filter-item .placeholder {
    color: #9ca3af;
    font-weight: 400;
    font-size: 0.78rem;
  }

  .filter-item .dropdown-arrow {
    color: #6846d1;
    font-size: 0.7rem;
  }

  .filter-item .dropdown-control:hover .dropdown-arrow,
  .filter-item .dropdown-control.open .dropdown-arrow {
    color: #6846d1;
  }

  /* Dropdown menu positioned outside card */
  .filter-item .dropdown-menu {
    position: absolute; /* Keep default absolute positioning */
    z-index: 99999 !important; /* Very high z-index to appear above all other cards and drawer content */
    border: 1px solid #e5e7eb; /* Subtle border */
    border-radius: 8px;
    box-shadow:
      0 4px 20px rgba(0, 0, 0, 0.12),
      0 0 0 1px rgba(104, 70, 209, 0.15); /* Elegant shadow with subtle purple outline */
    max-height: 280px;
    background: #ffffff;
    /* Ensure it spans full width of the filter item */
    left: 0;
    right: 0;
    margin-top: 6px;
  }

  /* Search input styling */
  .filter-item .search-input {
    border: none;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 0;
    font-size: 0.8rem;
    padding: 10px 32px 10px 12px;
    background: #fafafa;
    transition: all 0.25s ease;

    &:hover {
      background: #f5f3ff;
      border-bottom-color: #c4b5fd;
    }

    &:focus {
      outline: none;
      background: #faf9fe;
      border-bottom-color: #6846d1;
      border-bottom-width: 2px;
      padding-bottom: 9px; /* Adjust for thicker border */
    }

    &::placeholder {
      color: #9ca3af;
      font-size: 0.78rem;
      font-style: italic;
    }
  }

  /* Search container */
  .filter-item .search-container {
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
  }

  /* Search clear button */
  .filter-item .search-clear {
    color: #9ca3af;
    font-size: 0.75rem;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
      color: #6846d1;
      background-color: #f5f3ff;
      transform: scale(1.1);
    }
  }

  /* Options container scrollbar */
  .filter-item .options-container {
    max-height: 220px;
    padding: 4px 0;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f5f5f5;
      border-radius: 10px;
      margin: 4px 0;
    }

    &::-webkit-scrollbar-thumb {
      background: linear-gradient(180deg, #6846d1 0%, #8b5cf6 100%);
      border-radius: 10px;

      &:hover {
        background: linear-gradient(180deg, #5435b8 0%, #7c3aed 100%);
      }
    }
  }

  /* Individual option styling */
  .filter-item .option {
    font-size: 0.78rem;
    padding: 8px 12px;
    color: #4b5563;
    border-radius: 6px;
    margin: 2px 6px;
    min-height: 32px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;

    &:hover {
      background: #f5f3ff;
      color: #111827;
    }

    &.selected {
      background: linear-gradient(
        90deg,
        rgba(104, 70, 209, 0.1) 0%,
        rgba(104, 70, 209, 0.05) 100%
      );
      color: #6846d1;
      font-weight: 600;
      border-left: 3px solid #6846d1;
      padding-left: 9px;

      &:hover {
        background: linear-gradient(
          90deg,
          rgba(104, 70, 209, 0.15) 0%,
          rgba(104, 70, 209, 0.08) 100%
        );
      }
    }
  }

  /* No options message */
  .filter-item .no-options {
    padding: 20px;
    text-align: center;
    color: #9ca3af;
    font-size: 0.78rem;
  }

  /* Multi-Select Checkbox Dropdown - Additional Styles */
  /* Checkboxes inside multi-select dropdown options */
  .filter-item .option input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin-right: 10px;
    border: 1.5px solid #d1d5db;
    border-radius: 3px;
    cursor: pointer;
    accent-color: #6846d1;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
      border-color: #6846d1;
      background-color: #f5f3ff;
    }

    &:checked {
      background-color: #6846d1;
      border-color: #6846d1;
    }
  }

  .filter-item .option label {
    cursor: pointer;
    font-size: 0.8rem;
    color: #374151;
    flex: 1;
    user-select: none;
    line-height: 1.4;
    transition: color 0.2s ease;

    &:hover {
      color: #111827;
    }
  }

  .filter-item .select-all-option {
    font-weight: 600;
    padding: 10px 12px;
    border-bottom: 1px solid #f0f0f0;
    margin: 0;
    background: #fafafa;
    transition: background 0.2s ease;

    &:hover {
      background: #f5f3ff;
    }

    input[type='checkbox'] {
      margin-right: 8px;
    }

    label {
      font-size: 0.8rem;
      color: #374151;
      font-weight: 600;
    }
  }

  /* Dropdown header section */
  .filter-item .dropdown-header {
    border-bottom: 1px solid #f0f0f0;
    background: white;
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;
